/**
 * Sentry Session Replay, attached after load + idle on desktop only — and loaded from
 * Sentry's CDN instead of our bundle (improvement row I2, slice 1).
 *
 * Why not `Sentry.replayIntegration(...)` directly: that static reference keeps the whole
 * recorder (`@sentry-internal/replay`, ~40 kB gzip) in the entry chunk of every page load,
 * phones included, although it is only ever constructed on a desktop after idle. A dynamic
 * `import('@sentry/react')` does not help — the module is statically imported elsewhere, so
 * Rollup keeps it in the entry. `lazyLoadIntegration` fetches the recorder as a separate
 * script once it is wanted (row I2: entry 628.7 → 584.8 kB gzip).
 *
 * Accepted costs, unchanged from before: an error thrown before load + idle is reported
 * without a replay, and an ad blocker or offline client simply gets no replay — error
 * capture keeps running either way (the rejection is swallowed on purpose).
 */
import type { ReplayHost, ReplayOptions } from '../interfaces/SentryReplay';
import { redactRecordingEvent } from './sentryRedact';

export const REPLAY_OPTIONS: ReplayOptions = {
    maskAllText: false,
    maskAllInputs: true,
    mask: ['.sentry-mask', '[data-sentry-mask]'],
    networkRequestHeaders: ['Cache-Control'],
    networkResponseHeaders: ['Referrer-Policy'],
    // Network events record request addresses; the Walley status check carries a checkout token in its query.
    beforeAddRecordingEvent: redactRecordingEvent,
};

export type { ReplayHost, ReplayOptions };

/**
 * Loads and attaches Replay once the page is idle. Returns a promise that settles when the
 * attempt is over (resolved either way), so callers and tests never see a rejection.
 */
export function attachReplayWhenIdle(
    sentry: ReplayHost,
    runWhenIdle: (task: () => void) => unknown,
    smallScreen: boolean,
): Promise<void> {
    if (smallScreen) return Promise.resolve();
    return new Promise<void>(resolve => {
        runWhenIdle(() => {
            sentry.lazyLoadIntegration('replayIntegration')
                .then(replayIntegration => { sentry.addIntegration(replayIntegration(REPLAY_OPTIONS)); })
                .catch(() => { /* blocked or offline: no replay, nothing else changes */ })
                .finally(resolve);
        });
    });
}
